import { z } from 'zod';

import { parseTimestamp } from '../lib/date-time.js';

// ─── Status & kind ────────────────────────────────────────────────────────────

export const unifiedSuppressionStatusSchema = z.enum([
  'initiated',
  'in_progress',
  'finished',
  'stopped',
  'failed',
]);

export const unifiedSuppressionKindSchema = z.enum(['regular', 'broadband']);

export type UnifiedSuppressionStatus = z.infer<typeof unifiedSuppressionStatusSchema>;
export type UnifiedSuppressionKind = z.infer<typeof unifiedSuppressionKindSchema>;

const numberLikeSchema = z
  .union([z.number(), z.string()])
  .transform((value) => (typeof value === 'string' ? Number(value) : value))
  .pipe(z.number().refine(Number.isFinite));

const nullableNumberLikeSchema = z.preprocess(
  (value) => (value == null ? null : value),
  numberLikeSchema.nullable(),
);

// ─── DTO schema (raw API shape) ───────────────────────────────────────────────

const unifiedSuppressionDtoSchema = z.object({
  id: z.string(),
  kind: unifiedSuppressionKindSchema,
  deviceId: z.string(),
  type: z.enum(['analog', 'digital']),
  status: unifiedSuppressionStatusSchema,
  mode: z.enum(['solid', 'hopping', 'multi']).nullable(),
  // Frequency — mutually exclusive by kind
  channelFrequency: nullableNumberLikeSchema,
  channelFrequencyStart: nullableNumberLikeSchema,
  channelFrequencyStop: nullableNumberLikeSchema,
  channelFrequencyList: z.array(numberLikeSchema).nullable(),
  // Broadband-specific (null for regular)
  step: nullableNumberLikeSchema,
  stepStandingTimeMs: nullableNumberLikeSchema,
  bandwidth: nullableNumberLikeSchema,
  // Regular-specific (null for broadband)
  reason: z.string().nullish(),
  success: z.boolean().nullable(),
  offset: nullableNumberLikeSchema.default(null),
  priority: nullableNumberLikeSchema.default(null),
  // Shared
  duration: nullableNumberLikeSchema,
  isStopped: z.boolean(),
  createdAt: z.string(),
  startedAt: z.string().nullish(),
  finishedAt: z.string().nullish(),
});

// ─── Parsed schema (timestamps → Luxon DateTime) ─────────────────────────────

export const unifiedSuppressionSchema = unifiedSuppressionDtoSchema.transform((s, ctx) => {
  const createdAt = parseTimestamp(s.createdAt);

  if (!createdAt) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid timestamp',
      path: ['createdAt'],
    });

    return z.NEVER;
  }

  return {
    ...s,
    createdAt,
    startedAt: parseTimestamp(s.startedAt),
    finishedAt: parseTimestamp(s.finishedAt),
  };
});

export const unifiedSuppressionListSchema = z.object({
  suppressions: z.array(unifiedSuppressionSchema),
  total: z.number(),
});

export type UnifiedSuppression = z.infer<typeof unifiedSuppressionSchema>;
